import { useState } from "react";

import { getListQuotes } from "../api/dossier/getListQuotes";
import { filterQuotes, sortQuotes } from "../helpers/quotes";
import { useAuth } from "../providers/AuthProvider";
import { ListQuote } from "../types/Quote";

interface UseQuoteLimitProps {
  dossierId: string;
  primaryQuoteId: string | null;
  onRedirect: (overwriteQuoteId?: string) => void;
  onCancel?: () => void;
}

export const useQuoteLimit = ({
  dossierId,
  primaryQuoteId,
  onRedirect,
  onCancel,
}: UseQuoteLimitProps) => {
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const { getScopedPageConfig, inAppScope } = useAuth();

  const quotes = getListQuotes(dossierId, { disabled: !dossierId });

  const checkQuoteLimit = () => {
    if (!quotes.isSuccess) return false;

    const filteredQuotes = filterQuotes(quotes.data, inAppScope("internal"));
    const quoteMax: number = getScopedPageConfig("dossierMaxQuoteCount");
    const hasHitQuoteLimit =
      filteredQuotes && filteredQuotes.length >= quoteMax;

    return hasHitQuoteLimit;
  };

  const getOldestQuote = (): ListQuote | null => {
    if (!quotes.isSuccess) return null;
    const filteredQuotes = filterQuotes(quotes.data, inAppScope("internal"));
    const sortedQuotes = sortQuotes(
      filteredQuotes,
      "lastUpdated",
      primaryQuoteId
    );

    // Find the oldest quote that is not the primary quote
    return sortedQuotes && sortedQuotes.length > 0
      ? sortedQuotes[sortedQuotes.length - 1]
      : null;
  };

  const handleQuoteAction = (skipLimitCheck?: boolean) => {
    const hasHitQuoteLimit = checkQuoteLimit();

    if (hasHitQuoteLimit && !skipLimitCheck) {
      setShowOverwriteModal(true);
    } else {
      onRedirect();
    }
  };

  const handleOverwriteConfirm = () => {
    const oldestQuote = getOldestQuote();
    const overwriteQuoteId = oldestQuote?.identifiers.crmQuoteId;

    setShowOverwriteModal(false);
    onRedirect(overwriteQuoteId);
  };

  const handleOverwriteCancel = () => {
    setShowOverwriteModal(false);
    if (onCancel) {
      onCancel();
    }
    // Default: just close modal, no redirect
  };

  return {
    showOverwriteModal,
    handleQuoteAction,
    handleOverwriteConfirm,
    handleOverwriteCancel,
    oldestQuote: getOldestQuote(),
    isLoading: quotes.isLoading,
    isSuccess: quotes.isSuccess,
  };
};
